import type {Config,Parameter} from "./attenuation-contract";

export const dissolutionId="solution-preparation-dissolution";

const n=(label:string,unit:string,value:number,min:number,max:number,step?:number):Parameter=>({
  type:"number",label,unit,default:value,minimum:min,maximum:max,...(step?{step}:{})
});
const b=(label:string,value:boolean):Parameter=>({type:"boolean",label,default:value});
const s=(label:string,value:string,values:string[]):Parameter=>({type:"string",label,default:value,enum:values});

export const dissolutionSchema:Record<string,Parameter>={
  concentrationDisplay:s("Affichage des concentrations","amount",["amount","mass"]),
  soluteId:s("Soluté","NaCl",["NaCl","copperSulfate","glucose"]),
  targetVolumeMl:n("Volume cible","mL",100,50,250,1),
  targetConcentrationMolL:n("Concentration cible","mol·L⁻¹",0.1,0.001,0.2,0.001),
  flaskVolumeMl:n("Volume de la fiole","mL",100,50,250,1),
  balanceResolutionG:{...n("Résolution de la balance","g",0.001,0.001,0.01),enum:[0.001,0.01]},
  mode:s("Mode au démarrage","guided",["method","guided","practice"]),
  showMolarMass:b("Afficher la masse molaire",true),
  showTargetMass:b("Afficher la masse cible",false),
  allowHints:b("Autoriser les aides",true),
  showMicroscopicView:b("Vue des espèces dissoutes",false),
  enableMethodMode:b("Autoriser la fiche méthode",true),
  enableGuidedMode:b("Autoriser l’entraînement",true),
  enablePracticeMode:b("Autoriser la mise en pratique",true),
  showInstructionPanel:b("Afficher le panneau de consignes",true),
  showStepCounter:b("Afficher les étapes",true),
  showFinalReport:b("Afficher le bilan final",true),
  showGoodPracticesChecks:b("Inclure les bonnes pratiques au bilan",true),
  showMassCalculation:b("Proposer l’aide au calcul",true),
  coarseAdditionStepG:n("Prélèvement grossier","g",0.1,0.001,2,0.001),
  fineAdditionStepG:n("Prélèvement fin","g",0.005,0.001,0.1,0.001),
  allowOverfill:b("Autoriser le dépassement du trait",true),
  requireRinsing:b("Vérifier les rinçages",true),
  requireStopBalance:b("Vérifier l’extinction de la balance",true),
  requireCloseJar:b("Vérifier la fermeture du pot",true),
  requireReplaceCap:b("Vérifier le bouchage de la fiole",true),
};

export const dissolutionDefaults=():Config=>Object.fromEntries(
  Object.entries(dissolutionSchema).map(([key,p])=>[key,structuredClone(p.default)])
);

export function validateDissolutionConfig(input:unknown,fromUrl=false){
  const config=dissolutionDefaults(),errors:string[]=[];
  if(!input||typeof input!=="object"||Array.isArray(input))return {config,errors:["Configuration invalide."]};
  for(const [key,raw] of Object.entries(input as Record<string,unknown>)){
    const p=dissolutionSchema[key];
    if(!p){errors.push(`Paramètre inconnu : ${key}`);continue;}
    let value:any=raw;
    if(fromUrl&&typeof raw==="string"){
      value=p.type==="number"?Number(raw):p.type==="boolean"?(raw==="true"?true:raw==="false"?false:raw):raw;
    }
    const ok=typeof value===p.type &&
      (p.type!=="number" || (Number.isFinite(value)&&value>=p.minimum!&&value<=p.maximum!)) &&
      (!p.enum || p.enum.includes(value));
    if(!ok)errors.push(`Valeur invalide : ${p.label}`); else config[key]=value;
  }
  if(Number(config.targetVolumeMl)!==Number(config.flaskVolumeMl))errors.push("Le volume cible doit correspondre au volume de la fiole à trait unique.");
  if(Number(config.fineAdditionStepG)>Number(config.coarseAdditionStepG))errors.push("Le prélèvement fin doit être inférieur au prélèvement grossier.");
  const enabled={method:config.enableMethodMode===true,guided:config.enableGuidedMode===true,practice:config.enablePracticeMode===true};
  if(!enabled[String(config.mode) as keyof typeof enabled])errors.push("Le mode initial doit être autorisé.");
  const molarMass=String(config.soluteId)==="NaCl"?58.44:String(config.soluteId)==="copperSulfate"?249.68:180.16;
  const mass=Number(config.targetConcentrationMolL)*Number(config.targetVolumeMl)/1000*molarMass;
  if(mass<Number(config.balanceResolutionG))errors.push("La masse cible doit atteindre au moins une division de la balance.");
  return {config,errors};
}
